import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { INgSelectTemplate } from '@shared/modules/form-components/select/classes/INgSelectTemplate';
import { Subject } from 'rxjs';
import { DropdownPosition, NgSelectComponent } from '@ng-select/ng-select';
import { FormInputBaseComponent } from '../base/form-input-base.component';
import { StandardObject } from '@shared/classes/common/StandardObject';
import { CompareWithFn } from '@ng-select/ng-select/lib/ng-select.component';

@UntilDestroy()
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
})
export class SelectComponent
  extends FormInputBaseComponent<string | number, StandardObject>
  implements OnInit, OnDestroy {
  @ContentChild('labelTemplate', { static: true }) labelTemplateComponent: INgSelectTemplate;
  @ContentChild('optionsTemplate', { static: true }) optionsTemplateComponent: INgSelectTemplate;
  @ContentChild('footerTemplate', { static: true }) footerTemplateComponent: INgSelectTemplate;
  @ViewChild('select') selectComponent: NgSelectComponent;

  @Input() options: StandardObject[];
  @Input() searchable = false;
  @Input() clearable = false;
  @Input() multiple = false;
  @Input() bindLabel = 'label';
  @Input() bindValue = 'value';
  @Input() bindValueByPropertyNameToControl: string;
  @Input() notFoundText = 'common.not_found';
  @Input() customClass: string | string[] = 'custom-ng-select';
  @Input() hideSelected = false;
  @Input() closeOnSelect = true;
  @Input() typeaheadText = 'common.typeahead_text';
  @Input() addTagText = 'common.add_new';
  @Input() isHintShown = true;
  @Input() hintText: string;
  @Input() useTypeahead = false;
  @Input() placeholder = 'common.please_select';
  @Input() addTagFn: Function;
  @Input() dropdownPosition: DropdownPosition = 'bottom';
  @Input() searchFn: Function;
  @Input() appendTo: string;
  @Input() virtualScroll = false;
  @Input() outputSearchTerm = false;
  @Input() compareWith: CompareWithFn;

  @Output() typeaheadChange = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<StandardObject>();
  @Output() opened = new EventEmitter<void>();
  @Output() clicked = new EventEmitter<Event>();
  @Output() searchTerm = new EventEmitter<string>();

  footerTemplate: TemplateRef<INgSelectTemplate>;
  labelTemplate: TemplateRef<INgSelectTemplate>;
  optionsTemplate: TemplateRef<INgSelectTemplate>;

  typeahead$ = new Subject<string>();
  searchResults: {
    term: string;
    items: StandardObject[];
  };

  constructor(
    @Self()
    @Optional()
    private ngControl: NgControl,
    private renderer2: Renderer2
  ) {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.labelTemplate = this.labelTemplateComponent?.templateRef as TemplateRef<INgSelectTemplate>;
    this.optionsTemplate = this.optionsTemplateComponent
      ?.templateRef as TemplateRef<INgSelectTemplate>;
    this.footerTemplate = this.footerTemplateComponent
      ?.templateRef as TemplateRef<INgSelectTemplate>;

    this.typeahead$
      .asObservable()
      .pipe(untilDestroyed(this))
      .subscribe((value) => this.typeaheadChange.emit(value));

    const superCalled = super.ngOnInit();
    this.control = this.ngControl.control;

    if (this.bindValueByPropertyNameToControl) {
      this.onChange = this.customPropertyBind.bind(this);
    }
    return superCalled;
  }

  onDropdownOpen() {
    this.opened.emit();

    // when using appendTo, we need to add our class manually to "ng-dropdown-panel"
    // because the "ng-dropdown-panel" won't be in the ng-select element anymore,
    //    (that originally has the class we can reference)
    // so this is the only way to style the dropdown element
    setTimeout(() => {
      if (this.appendTo) {
        this.renderer2.addClass(document.querySelector('ng-dropdown-panel'), 'base-dropdown-panel');
      }
    });
  }

  onSearchChange(event: { term: string; items: StandardObject[] }) {
    this.searchResults = event;

    if (this.outputSearchTerm) {
      this.searchTerm.emit(event.term);
    }
  }

  onDropdownClose() {
    this.searchResults = null;
  }

  private customPropertyBind(value: StandardObject): void {
    if (!value) {
      this.value = null;
      this.control?.setValue(null);
      return;
    }

    if (!value || value[this.bindValueByPropertyNameToControl] === undefined) {
      console.warn(`Value to bind with ${this.bindValueByPropertyNameToControl} is undefined!`);
      return;
    }
    this.control?.setValue(value[this.bindValueByPropertyNameToControl]);
    this.value = value[this.bindValue] as string | number;
  }

  ngOnDestroy(skip?: boolean) {
    this.typeahead$.complete();
    return super.ngOnDestroy(skip);
  }
}
